import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useClinicAuth } from './ClinicAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportContext = createContext();

export const useReport = () => {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error('useReport must be used within ReportProvider');
  }
  return context;
};

export const ReportProvider = ({ children }) => {
  const { token, hasActiveSubscription, user } = useClinicAuth();
  const [drafts, setDrafts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [draftStats, setDraftStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.role === 'clinic_admin' || user?.role === 'shs_admin';
  }, [user?.role]);

  // Fetch usage stats
  const fetchUsage = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/report/usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch usage' };
    }
  }, [token]);

  // Fetch draft stats (for dashboard tabs)
  const fetchDraftStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/report/drafts/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraftStats(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch stats' };
    }
  }, [token]);

  // Fetch drafts list with status_filter support
  const fetchDrafts = useCallback(async (filters = {}) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status_filter) params.append('status_filter', filters.status_filter);
      if (filters.status) params.append('status', filters.status);
      if (filters.study_type) params.append('study_type', filters.study_type);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const response = await axios.get(`${API_URL}/api/report/drafts?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDrafts(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch drafts' };
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Helper function for auth headers
  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Create new draft
  const createDraft = async (draftData) => {
    try {
      const response = await axios.post(`${API_URL}/api/report/drafts`, draftData, authHeaders());
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create draft' };
    }
  };

  // Get draft details
  const getDraft = async (draftId) => {
    try {
      const response = await axios.get(`${API_URL}/api/report/drafts/${draftId}`, authHeaders());
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch draft' };
    }
  };

  // Get draft versions
  const getDraftVersions = async (draftId) => {
    try {
      const response = await axios.get(`${API_URL}/api/report/drafts/${draftId}/versions`, authHeaders());
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch versions' };
    }
  };

  // Generate draft
  const generateDraft = async (draftId, confirmationChecked, additionalNotes = null) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/report/drafts/${draftId}/generate`,
        { confirmation_checked: confirmationChecked, additional_notes: additionalNotes },
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Generation failed' };
    }
  };

  // Update draft
  const updateDraft = async (draftId, content, status = null) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/report/drafts/${draftId}`,
        { content, status },
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update draft' };
    }
  };

  // Export draft (supports docx/pdf binary downloads)
  const exportDraft = async (draftId, format = 'txt') => {
    try {
      if (format === 'docx' || format === 'pdf') {
        // Binary file download
        const response = await axios.post(
          `${API_URL}/api/report/drafts/${draftId}/export?format=${format}`,
          {},
          { ...authHeaders(), responseType: 'blob' }
        );
        
        // Create download link
        const blob = new Blob([response.data], { 
          type: format === 'docx' 
            ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            : 'application/pdf'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `report_draft_${draftId.substring(0, 8)}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        return { success: true };
      } else {
        // Text/markdown - return content
        const response = await axios.post(
          `${API_URL}/api/report/drafts/${draftId}/export?format=${format}`,
          {},
          authHeaders()
        );
        return { success: true, data: response.data };
      }
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Export failed' };
    }
  };

  // Delete draft
  const deleteDraft = async (draftId) => {
    try {
      await axios.delete(`${API_URL}/api/report/drafts/${draftId}`, authHeaders());
      setDrafts(drafts.filter(d => d.id !== draftId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to delete draft' };
    }
  };

  // === P2-B: Archive Mode ===

  // Finalize draft
  const finalizeDraft = async (draftId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/report/drafts/${draftId}/finalize`,
        {},
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to finalize draft' };
    }
  };

  // Archive draft
  const archiveDraft = async (draftId, reason = null) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/report/drafts/${draftId}/archive`,
        { reason },
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to archive draft' };
    }
  };

  // Unarchive draft (admin only)
  const unarchiveDraft = async (draftId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/report/drafts/${draftId}/unarchive`,
        {},
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to unarchive draft' };
    }
  };

  // === P2-A: Favorite Templates ===

  // Fetch templates
  const fetchTemplates = useCallback(async (studyType = null) => {
    if (!token) return;
    try {
      const params = studyType ? `?study_type=${studyType}` : '';
      const response = await axios.get(`${API_URL}/api/report/templates${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch templates' };
    }
  }, [token]);

  // Get single template
  const getTemplate = async (templateId) => {
    try {
      const response = await axios.get(`${API_URL}/api/report/templates/${templateId}`, authHeaders());
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to fetch template' };
    }
  };

  // Create template (admin only)
  const createTemplate = async (templateData) => {
    try {
      const response = await axios.post(`${API_URL}/api/report/templates`, templateData, authHeaders());
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to create template' };
    }
  };

  // Update template (admin only)
  const updateTemplate = async (templateId, templateData) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/report/templates/${templateId}`,
        templateData,
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update template' };
    }
  };

  // Delete template (admin only)
  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/api/report/templates/${templateId}`, authHeaders());
      setTemplates(templates.filter(t => t.id !== templateId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to delete template' };
    }
  };

  // Apply template (pre-fill form)
  const applyTemplate = async (templateId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/report/templates/${templateId}/apply`,
        {},
        authHeaders()
      );
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to apply template' };
    }
  };

  return (
    <ReportContext.Provider
      value={{
        drafts,
        usage,
        draftStats,
        templates,
        loading,
        isAdmin,
        fetchUsage,
        fetchDraftStats,
        fetchDrafts,
        createDraft,
        getDraft,
        getDraftVersions,
        generateDraft,
        updateDraft,
        exportDraft,
        deleteDraft,
        // P2-B: Archive
        finalizeDraft,
        archiveDraft,
        unarchiveDraft,
        // P2-A: Templates
        fetchTemplates,
        getTemplate,
        createTemplate,
        updateTemplate,
        deleteTemplate,
        applyTemplate
      }}
    >
      {children}
    </ReportContext.Provider>
  );
};
